// src/pages/Dashboard/Balance/components/TopOutstanding.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import styles from './TopOutstanding.module.css';
import { balanceApi } from '../../../../api/balance/balanceApi';
import * as XLSX from 'xlsx';

const TopOutstanding = () => {
  const [salesPersons, setSalesPersons] = useState([]);
  const [selectedSlpCode, setSelectedSlpCode] = useState('');
  const [selectedSlpName, setSelectedSlpName] = useState('');
  const [allCustomers, setAllCustomers] = useState([]);
  const [displayedCustomers, setDisplayedCustomers] = useState([]);
  const [topCount, setTopCount] = useState(20);
  const [sortBy, setSortBy] = useState('overdue'); // 'overdue' or 'outstanding'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredSalesPersons, setFilteredSalesPersons] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('all'); // NEW: Region filter
  const dropdownRef = useRef(null);

  const topCountOptions = [10, 20, 30, 40, 50, 100];

  // Memoized calculation for unique customers
  const uniqueCustomers = useMemo(() => {
    const uniqueMap = new Map();
    allCustomers.forEach(customer => {
      const key = `${customer.cardCode}-${customer.region || 'no-region'}`;
      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, customer);
      }
    });
    return Array.from(uniqueMap.values());
  }, [allCustomers]);

  // Memoized calculation for available regions
  const availableRegions = useMemo(() => {
    const regions = new Set(uniqueCustomers.map(c => c.region).filter(Boolean));
    return ['all', ...Array.from(regions).sort()];
  }, [uniqueCustomers]);

  // Memoized calculation for filtered customers by region
  const regionFilteredCustomers = useMemo(() => {
    if (selectedRegion === 'all') {
      return uniqueCustomers;
    }
    return uniqueCustomers.filter(customer => customer.region === selectedRegion);
  }, [uniqueCustomers, selectedRegion]);

  // Memoized calculation for sorted customers based on sortBy
  const sortedCustomers = useMemo(() => {
    const customers = [...regionFilteredCustomers];
    if (sortBy === 'overdue') {
      return customers.sort((a, b) => (b.overdueAmount || 0) - (a.overdueAmount || 0));
    } else {
      return customers.sort((a, b) => b.totalOutstanding - a.totalOutstanding);
    }
  }, [regionFilteredCustomers, sortBy]);

  // Memoized calculation for non-zero customers
  const nonZeroCustomers = useMemo(() => {
    return sortedCustomers.filter(customer => customer.totalOutstanding > 0);
  }, [sortedCustomers]);

  // Memoized calculation for top N non-zero customers to highlight
  const topNonZeroCustomers = useMemo(() => {
    const topN = Math.min(10, nonZeroCustomers.length);
    return nonZeroCustomers.slice(0, topN).map(c => `${c.cardCode}-${c.region || 'no-region'}`);
  }, [nonZeroCustomers]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchSalesPersons();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredSalesPersons(salesPersons);
    } else {
      const filtered = salesPersons.filter(sp =>
        sp.slpName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredSalesPersons(filtered);
    }
  }, [searchTerm, salesPersons]);

  useEffect(() => {
    if (sortedCustomers.length > 0) {
      setDisplayedCustomers(sortedCustomers.slice(0, topCount));
    }
  }, [sortedCustomers, topCount]);

  useEffect(() => {
    // Reset region filter when changing salesperson
    setSelectedRegion('all');
  }, [selectedSlpCode]);

  const fetchSalesPersons = async () => {
    try {
      setLoading(true);
      const data = await balanceApi.getSalesPersons();
      setSalesPersons(data);
      setFilteredSalesPersons(data);
      console.log('✅ Sales persons loaded:', data.length);
    } catch (err) {
      setError('Failed to load sales persons');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTopOutstanding = async (slpCode, slpName) => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔍 Fetching top outstanding for SlpCode:', slpCode);
      
      const data = await balanceApi.getTopOutstanding(slpCode);
      
      console.log('✅ Top outstanding loaded:', data.length, 'customers');
      console.log('✅ Unique customers:', new Set(data.map(d => d.cardCode)).size);
      
      // Log regions for debugging
      const regions = [...new Set(data.map(c => c.region).filter(Boolean))];
      console.log('✅ Regions found:', regions);
      
      setAllCustomers(data);
      setSelectedSlpName(slpName);
    } catch (err) {
      setError('Failed to load top outstanding customers');
      console.error(err);
      setAllCustomers([]);
      setDisplayedCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSalesPersonSelect = (sp) => {
    setSelectedSlpCode(sp.slpCode);
    setSearchTerm(sp.slpName);
    setShowDropdown(false);
    fetchTopOutstanding(sp.slpCode, sp.slpName);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setShowDropdown(true);
    if (selectedSlpCode) {
      setSelectedSlpCode('');
      setAllCustomers([]);
      setDisplayedCustomers([]);
    }
  };

  const handleSearchFocus = () => {
    setShowDropdown(true);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSelectedSlpCode('');
    setSelectedSlpName('');
    setAllCustomers([]);
    setDisplayedCustomers([]);
    setSelectedRegion('all');
    setShowDropdown(false);
  };

  const handleTopCountChange = (e) => {
    setTopCount(parseInt(e.target.value));
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
  };

  const handleRegionChange = (e) => {
    setSelectedRegion(e.target.value);
  };

  const handleExportExcel = () => {
    try {
      if (displayedCustomers.length === 0) {
        alert('No data to export');
        return;
      }

      const exportData = displayedCustomers.map((customer, index) => ({
        'Rank': index + 1,
        'Sales Employee': selectedSlpName,
        'Customer': customer.customerName,
        'Customer Code': customer.cardCode,
        'Region': customer.region || 'Not Specified',
        'Overdue': customer.overdueAmount || 0,
        'Total Outstanding': customer.totalOutstanding
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Top Outstanding');
      
      const sortLabel = sortBy === 'overdue' ? 'Overdue' : 'Outstanding';
      const regionLabel = selectedRegion === 'all' ? 'All' : selectedRegion;
      const filename = `Top_Outstanding_${selectedSlpName.replace(/\s+/g, '_')}_Region_${regionLabel}_SortBy_${sortLabel}.xlsx`;
      XLSX.writeFile(workbook, filename);
      
      console.log('✅ Exported', displayedCustomers.length, 'customers to Excel');
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      alert('Failed to export data');
    }
  };

  const formatCurrency = (amount) => {
    return '₹' + (amount || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const shouldHighlight = (customer) => {
    const key = `${customer.cardCode}-${customer.region || 'no-region'}`;
    return topNonZeroCustomers.includes(key);
  };

  // Calculate region-wise summary
  const regionSummary = useMemo(() => {
    const summary = {};
    uniqueCustomers.forEach(customer => {
      const region = customer.region || 'Unknown';
      if (!summary[region]) {
        summary[region] = {
          count: 0,
          totalOutstanding: 0,
          totalOverdue: 0
        };
      }
      summary[region].count++;
      summary[region].totalOutstanding += customer.totalOutstanding;
      summary[region].totalOverdue += (customer.overdueAmount || 0);
    });
    return summary;
  }, [uniqueCustomers]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.pageTitle}>Top Outstanding Customers</h2>
        </div>
      </div>

      <div className={styles.filtersSection}>
        <div className={styles.filterRow}>
          <div className={styles.filterGroup} ref={dropdownRef}>
            <label className={styles.filterLabel}>Select Sales Person:</label>
            <div className={styles.searchInputWrapper}>
              <span className={styles.searchIcon}>🔍</span>
              <input
                type="text"
                placeholder="Search sales person..."
                value={searchTerm}
                onChange={handleSearchChange}
                onFocus={handleSearchFocus}
                disabled={loading}
                className={styles.searchInput}
              />
              {searchTerm && (
                <button onClick={handleClearSearch} className={styles.clearSearchButton} title="Clear search">
                  ✕
                </button>
              )}
              
              {showDropdown && filteredSalesPersons.length > 0 && (
                <div className={styles.dropdownMenu}>
                  {filteredSalesPersons.map((sp) => (
                    <div
                      key={sp.slpCode}
                      onClick={() => handleSalesPersonSelect(sp)}
                      className={`${styles.dropdownItem} ${sp.slpCode === selectedSlpCode ? styles.dropdownItemActive : ''}`}
                    >
                      <div className={styles.dropdownItemName}>{sp.slpName}</div>
                      <div className={styles.dropdownItemCode}>Code: {sp.slpCode}</div>
                    </div>
                  ))}
                </div>
              )}
              
              {showDropdown && searchTerm && filteredSalesPersons.length === 0 && (
                <div className={styles.dropdownMenu}>
                  <div className={styles.dropdownNoResults}>No sales person found</div>
                </div>
              )}
            </div>
          </div>

          {selectedSlpCode && allCustomers.length > 0 && (
            <>
              <div className={styles.sortGroup}>
                <label className={styles.filterLabel}>Sort by:</label>
                <select value={sortBy} onChange={handleSortChange} className={styles.sortSelect}>
                  <option value="overdue">Overdue</option>
                  <option value="outstanding">Total Outstanding</option>
                </select>
              </div>

              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>Show Top:</label>
                <select value={topCount} onChange={handleTopCountChange} className={styles.topCountSelect}>
                  {topCountOptions.map((count) => (
                    <option key={count} value={count}>
                      {count}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>Region:</label>
                <select value={selectedRegion} onChange={handleRegionChange} className={styles.regionSelect}>
                  {availableRegions.map((region) => (
                    <option key={region} value={region}>
                      {region === 'all' ? 'All Regions' : region}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.excelButtonWrapper}>
                <button onClick={handleExportExcel} disabled={loading} className={styles.excelButton}>
                  <span className={styles.excelIcon}>📊</span>
                  Export Excel
                </button>
              </div>
            </>
          )}
        </div>

        {selectedSlpCode && allCustomers.length > 0 && (
          <div className={styles.summaryRow}>
            <div className={styles.summaryText}>
              Showing top <strong>{displayedCustomers.length}</strong> of{' '}
              <strong>{selectedRegion === 'all' ? uniqueCustomers.length : regionFilteredCustomers.length}</strong> customers
              {nonZeroCustomers.length > 0 && (
                <span className={styles.nonZeroIndicator}>
                  {' '}(<strong>{nonZeroCustomers.length}</strong> with non-zero outstanding)
                </span>
              )}
              {selectedRegion !== 'all' && (
                <span className={styles.regionIndicator}>
                  {' '}in <strong>{selectedRegion}</strong> region
                </span>
              )}
            </div>
            <div className={styles.totalAmounts}>
              <div className={styles.totalOverdue}>
                Total Overdue:{' '}
                <span className={styles.amountRed}>
                  {formatCurrency(
                    displayedCustomers.reduce((sum, c) => sum + (c.overdueAmount || 0), 0)
                  )}
                </span>
              </div>
              <div className={styles.totalOutstanding}>
                Total Balance:{' '}
                <span className={styles.amountBlue}>
                  {formatCurrency(
                    displayedCustomers.reduce((sum, c) => sum + c.totalOutstanding, 0)
                  )}
                </span>
              </div>
            </div>
          </div>
        )}

       
      </div>

      {error && <div className={styles.errorMessage}>{error}</div>}

      {loading && (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner} />
          <div className={styles.loadingText}>Loading data...</div>
        </div>
      )}

      {!loading && !selectedSlpCode && (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>👤</div>
          <h3 className={styles.emptyTitle}>Select a Sales Person</h3>
          <p className={styles.emptyText}>
            Search and select a sales person from the dropdown above to view their top outstanding customers
          </p>
        </div>
      )}

      {!loading && selectedSlpCode && allCustomers.length === 0 && (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📊</div>
          <h3 className={styles.emptyTitle}>No Outstanding Balances</h3>
          <p className={styles.emptyText}>
            This sales person has no customers with outstanding balances
          </p>
        </div>
      )}

      {!loading && displayedCustomers.length > 0 && (
        <div className={styles.customersContainer}>
          <div className={styles.customersList}>
            <div className={styles.tableHeader}>
              <div className={styles.tableHeaderCell}>#</div>
              <div className={styles.tableHeaderCell}>Customer</div>
              <div className={styles.tableHeaderCell}>Region</div>
              <div className={`${styles.tableHeaderCell} ${styles.tableHeaderCellRight}`}>Overdue</div>
              <div className={`${styles.tableHeaderCell} ${styles.tableHeaderCellRight}`}>Total Outstanding</div>
            </div>
            
            {displayedCustomers.map((customer, index) => {
              const hasZeroBalance = customer.totalOutstanding === 0;
              const isHighlighted = shouldHighlight(customer);
              
              return (
                <div 
                  key={`${customer.cardCode}-${customer.region || 'no-region'}-${index}`}
                  className={`${styles.customerCard} ${hasZeroBalance ? styles.zeroBalance : ''} ${isHighlighted ? styles.highlighted : ''}`}
                >
                  <div className={styles.rank}>
                    #{index + 1}
                  </div>
                  <div className={styles.customerInfo}>
                    <div className={`${styles.customerName} ${hasZeroBalance ? styles.zeroBalanceText : ''}`}>
                      {customer.customerName}
                    </div>
                    <div className={styles.customerCode}>{customer.cardCode}</div>
                  </div>
                  <div className={styles.regionInfo}>
                    <span className={`${styles.regionBadge} ${customer.region ? styles.regionBadgeFilled : styles.regionBadgeEmpty}`}>
                      {customer.region || 'No Region'}
                    </span>
                  </div>
                  <div className={`${styles.overdueAmount} ${(customer.overdueAmount || 0) > 0 ? styles.overdueAmountActive : ''}`}>
                    {formatCurrency(customer.overdueAmount || 0)}
                  </div>
                  <div className={`${styles.outstandingAmount} ${hasZeroBalance ? styles.zeroBalanceAmount : ''}`}>
                    {formatCurrency(customer.totalOutstanding)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TopOutstanding;